import React, { useEffect, useState } from 'react';
import {
  AddUniversal,
  QuarterLoaderUniversal,
  RefreshUniversal,
  SubtractUniversal,
} from '@duxton-web/icons';
import {
  Badge,
  Button,
  Card,
  Flex,
  Segmented,
  Table,
  Tag,
  Tooltip,
  Typography,
} from '@duxton-web/ui';
import type { ColumnsType } from '@duxton-web/ui/es/table';
import scribe from '@grab-x/scribe-web-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { useExperimentContext } from '../../contexts/experiment-context';
import ExpandedRow from './components/ExpandedRow';
import HealthCard from './components/HealthCard';
import SummaryView from './components/SummaryView';
import type { ScorecardMetricRow, ScorecardStatus } from './types';
import {
  formatDate,
  formatDateTime,
  getApiErrorArg,
  getMetricLevelTag,
  getMidasMetricUrl,
  getScorecardErrorMessage,
  getStatusBadge,
  isNewMetric,
} from './utils';
import { axaScorecardEvents } from '@/common/Scribe/EventsList';
import { COMPLETED } from '@/constants';
import { queryKeys } from '@/hooks/constants';
import { useGetScorecard } from '@/hooks/usePlannerQueries';

type ScorecardViewMode = 'summary' | 'details';

const SCORECARD_VIEW_QUERY_PARAM = 'scorecardView';

const getScorecardViewModeFromSearch = (search: string): ScorecardViewMode => {
  const requestedMode = new URLSearchParams(search).get(
    SCORECARD_VIEW_QUERY_PARAM,
  );

  if (requestedMode === 'summary') {
    return 'summary';
  }

  if (requestedMode === 'details') {
    return 'details';
  }

  return 'summary';
};

const AxaScorecardsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { experiment, analysis } = useExperimentContext();
  const experimentName = experiment?.name || '';
  const isExperimentCompleted = experiment?.state === COMPLETED;

  const hasMetrics = !!(
    analysis?.analysisConfig?.guardrail_metrics?.length ||
    analysis?.analysisConfig?.primary_metrics?.length ||
    analysis?.analysisConfig?.secondary_metrics?.length
  );
  const supersetLink = analysis?.supersetLink;

  const { data, isLoading, isFetching, isError, error } =
    useGetScorecard(experimentName);

  const handleRefresh = () => {
    // Invalidate both the overall scorecard and all metric scorecard queries
    queryClient.invalidateQueries({
      queryKey: queryKeys.planner.scorecard(experimentName),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.planner.metricScorecards(experimentName),
    });
  };

  const [viewMode, setViewMode] = useState<ScorecardViewMode>(() =>
    getScorecardViewModeFromSearch(location.search),
  );
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    const normalizedViewMode = getScorecardViewModeFromSearch(location.search);

    setViewMode((currentViewMode) =>
      currentViewMode === normalizedViewMode
        ? currentViewMode
        : normalizedViewMode,
    );
  }, [location.search]);

  useEffect(() => {
    if (!experimentName) return;
    const event = axaScorecardEvents.visitTab;
    scribe.sendEvent(event.name, {
      STATE_NAME: event.state,
      experiment_name: experimentName,
    });
  }, [experimentName]);

  useEffect(() => {
    // Only manage the URL param when on the scorecard tab
    const isOnScorecardTab = location.pathname.endsWith('/axa-scorecards');
    if (!isOnScorecardTab) {
      // Clean up the param when not on scorecard tab
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.has(SCORECARD_VIEW_QUERY_PARAM)) {
        searchParams.delete(SCORECARD_VIEW_QUERY_PARAM);
        const newSearch = searchParams.toString();
        window.history.replaceState(
          null,
          '',
          `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`,
        );
      }
      return;
    }

    // Add/update the param when on scorecard tab
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get(SCORECARD_VIEW_QUERY_PARAM) === viewMode) {
      return;
    }

    searchParams.set(SCORECARD_VIEW_QUERY_PARAM, viewMode);
    navigate(
      {
        pathname: window.location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true },
    );
  }, [location.pathname, navigate, viewMode]);

  const hasFailures = (data?.health.failedCount ?? 0) > 0;

  const toggleExpand = (record: ScorecardMetricRow) => {
    const isExpanding = !expandedRows.has(record.metricId);

    if (isExpanding) {
      const event = axaScorecardEvents.clickExpandMetric;
      scribe.sendEvent(event.name, {
        STATE_NAME: event.state,
        experiment_name: experimentName,
        metric_id: record.metricId,
        metric_name: record.metricDisplayName,
        metric_level: record.metricLevel,
        metric_status: record.status,
      });
    }

    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(record.metricId)) {
        next.delete(record.metricId);
      } else {
        next.add(record.metricId);
      }
      return next;
    });
  };

  const columns: ColumnsType<ScorecardMetricRow> = [
    {
      title: '',
      key: 'expand',
      width: 48,
      render: (_value: unknown, record: ScorecardMetricRow) => {
        const isExpanded = expandedRows.has(record.metricId);
        const isExpandable = record.status !== 'NOT_STARTED';

        return (
          <Flex align="center" justify="center">
            <Button
              type="text"
              size="small"
              shape="circle"
              disabled={!isExpandable}
              onClick={() => isExpandable && toggleExpand(record)}
              icon={isExpanded ? <SubtractUniversal /> : <AddUniversal />}
              style={{
                width: 20,
                height: 20,
                minWidth: 20,
                fontSize: 10,
                border: '1px solid #d9d9d9',
                color: '#8c8c8c',
              }}
            />
          </Flex>
        );
      },
    },
    {
      title: 'Metric Name',
      key: 'metricName',
      width: 450,
      render: (_: unknown, record: ScorecardMetricRow) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Link
            strong
            href={getMidasMetricUrl(record.metricKey)}
            target="_blank"
            rel="noreferrer"
          >
            {record.metricDisplayName}
          </Typography.Link>
          {isNewMetric(record.createdAt) && (
            <Tag color="success" style={{ margin: 0 }}>
              New
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'metricLevel',
      key: 'type',
      width: 90,
      render: (level: string) => {
        const { label, color } = getMetricLevelTag(level);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Results Window',
      key: 'resultsWindow',
      width: 200,
      render: (_value: unknown, record: ScorecardMetricRow) => (
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {formatDate(record.resultsWindow.startDate)} →{' '}
          {formatDate(record.resultsWindow.endDate)}
        </Typography.Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 220,
      render: (status: ScorecardStatus, record: ScorecardMetricRow) => {
        const { label, status: badgeStatus } = getStatusBadge(
          status,
          record.createdAt,
        );
        return <Badge status={badgeStatus} text={label} />;
      },
    },
    ...(hasFailures
      ? [
          {
            title: 'Failure reason',
            dataIndex: 'failureReason',
            key: 'failureReason',
            width: 360,
            render: (reason: string) =>
              reason ? (
                <Tooltip title={reason}>
                  <Flex vertical gap={2}>
                    <Typography.Text
                      type="danger"
                      style={{
                        fontSize: 13,
                        display: 'block',
                        maxWidth: 340,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {reason}
                    </Typography.Text>
                    <Link
                      to="https://techdocs.grab.com/product-docs/grabx/grabx-user-guide/axa/scorecard-troubleshooting-guide/"
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 12,
                        color: '#4096ff',
                      }}
                    >
                      Troubleshoot here →
                    </Link>
                  </Flex>
                </Tooltip>
              ) : (
                '—'
              ),
          },
        ]
      : []),
    {
      title: 'Last Sync Time',
      dataIndex: 'lastSyncTime',
      key: 'lastSyncTime',
      width: 200,
      render: (timestamp: string | null) => (
        <Typography.Text style={{ fontSize: 13 }}>
          {formatDateTime(timestamp)}
        </Typography.Text>
      ),
    },
  ];

  const containerStyle = {
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    overflow: 'hidden auto' as const,
    background: '#f5f5f5',
  };

  if (isLoading) {
    return (
      <div style={containerStyle}>
        <Card>
          <Typography.Title level={5} style={{ margin: '0 0 16px 0' }}>
            Scorecard
          </Typography.Title>
          <Flex justify="center" style={{ padding: '32px 24px' }}>
            <Typography.Text type="secondary">
              Loading scorecard data…
            </Typography.Text>
          </Flex>
        </Card>
      </div>
    );
  }

  if (isError || !data) {
    const errorMsg = getScorecardErrorMessage(error);
    const isMuted = getApiErrorArg(error) === 'not_on_demand_experiment';

    return (
      <div style={containerStyle}>
        <Card>
          <Typography.Title level={5} style={{ margin: '0 0 16px 0' }}>
            Scorecard
          </Typography.Title>
          <Flex justify="center" style={{ padding: '32px 24px' }}>
            <Typography.Text type={isMuted ? 'secondary' : 'danger'}>
              {errorMsg}
            </Typography.Text>
          </Flex>
        </Card>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <Card>
        <Typography.Title level={5} style={{ margin: '0 0 16px 0' }}>
          Scorecard
        </Typography.Title>

        <HealthCard data={data} />

        <Flex
          justify="space-between"
          align="center"
          style={{ marginTop: 12, marginBottom: 12 }}
        >
          <Segmented
            size="small"
            value={viewMode}
            options={[
              { label: 'Summary', value: 'summary' },
              { label: 'Details', value: 'details' },
            ]}
            onChange={(value) => setViewMode(value as ScorecardViewMode)}
          />
          <Button
            type="text"
            size="small"
            icon={
              isFetching ? (
                <QuarterLoaderUniversal spin />
              ) : (
                <RefreshUniversal />
              )
            }
            onClick={handleRefresh}
            disabled={isFetching}
          >
            Refresh
          </Button>
        </Flex>

        <div
          style={{
            background: '#ffffff',
            borderRadius: 8,
            border: '1px solid #e8e8e8',
            overflow: 'hidden',
          }}
        >
          {viewMode === 'summary' ? (
            <SummaryView
              experimentName={experimentName}
              metrics={data.metrics}
              enabled={viewMode === 'summary'}
              isExperimentCompleted={isExperimentCompleted}
              analysisConfig={analysis?.analysisConfig}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={data.metrics}
              rowKey="metricId"
              pagination={false}
              size="middle"
              loading={isFetching}
              expandable={{
                expandedRowKeys: Array.from(expandedRows),
                expandedRowRender: (record) => (
                  <ExpandedRow
                    experimentName={experimentName}
                    record={record}
                    analysisConfig={analysis?.analysisConfig}
                  />
                ),
                showExpandColumn: false,
                rowExpandable: (record) => record.status !== 'NOT_STARTED',
              }}
            />
          )}
        </div>

        <Flex
          justify="flex-end"
          style={{
            marginTop: 16,
            paddingTop: 16,
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Tooltip
            title={
              !hasMetrics
                ? 'No metric associated with the experiment.'
                : undefined
            }
          >
            <Button
              type="primary"
              size="small"
              href={hasMetrics ? supersetLink : undefined}
              target="_blank"
              disabled={!hasMetrics}
              style={
                hasMetrics
                  ? { backgroundColor: '#52c41a', borderColor: '#52c41a' }
                  : undefined
              }
              onClick={() => {
                if (hasMetrics) {
                  const event = axaScorecardEvents.clickViewFullResults;
                  scribe.sendEvent(event.name, {
                    STATE_NAME: event.state,
                    experiment_name: experimentName,
                    superset_link: supersetLink,
                  });
                }
              }}
            >
              View Full AXA Results here →
            </Button>
          </Tooltip>
        </Flex>
      </Card>
    </div>
  );
};

export default AxaScorecardsView;
